import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, Observable, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@shared';
import {
  IJurisdiction,
  IJurisdictionApplyParams,
  IJurisdictionApplyResult,
  IJurisdictionDiff,
  ITaxOptions,
} from '@shared/model/project';

@Injectable({
  providedIn: 'root',
})
export class JurisdictionService {
  private _baseUrl: string = `${environment.apiUrl}/project_taxes`;
  private _projectsUrl: string = `${environment.apiUrl}/projects/projects`;

  constructor(
    private _http: HttpClient,
    private _translate: TranslateService,
    private _snackbarService: SnackbarService,
  ) {}

  // NOTE: ставки НДС и доступные режимы страны проекта (этап 1)
  getTaxOptions(projectId: number): Observable<ITaxOptions> {
    return this._http.get<ITaxOptions>(`${this._baseUrl}/tax_options/${projectId}/`);
  }

  getJurisdictions(): Observable<IJurisdiction[]> {
    return this._http.get<IJurisdiction[]>(`${this._baseUrl}/jurisdictions/`);
  }

  getJurisdictionDiff(projectId: number): Observable<IJurisdictionDiff> {
    return this._http.get<IJurisdictionDiff>(`${this._baseUrl}/jurisdiction_diff/${projectId}/`);
  }

  applyJurisdiction(projectId: number, params: IJurisdictionApplyParams): Observable<IJurisdictionApplyResult> {
    return this._http.post<IJurisdictionApplyResult>(`${this._baseUrl}/jurisdiction_apply/${projectId}/`, {
      ...params,
    });
  }

  // NOTE: бэкенд отдает name только по-русски, английское название страны - в name_en
  getCountryName(jurisdiction?: IJurisdiction | null): string {
    if (!jurisdiction) return '';
    return (this._translate.currentLang === 'en' && jurisdiction.name_en) || jurisdiction.name;
  }

  // NOTE: F9 - уведомление по вариантам, у которых apply сменил режим: по фразе на вариант в одну строку
  // (снекбар не переносит строки). Название режима берем из diff, который показывали в диалоге (по variant_id).
  // Тип - error, если есть перевод на минимальный режим (switch), иначе success (только возврат режима)
  getRegimeChangesNotice(
    result: IJurisdictionApplyResult,
    diff?: IJurisdictionDiff | null,
  ): { text: string; type: 'success' | 'error' } | null {
    const changes = result?.regime_changes ?? [];
    if (!changes.length) return null;
    const country = this.getCountryName(result.jurisdiction ?? diff?.jurisdiction);
    const text = changes
      .map(({ variant_id, variant_name, kind }) => {
        const regime = diff?.variants?.find(({ id }) => id === variant_id)?.regime?.label ?? '';
        const phrase =
          kind === 'restore'
            ? this._translate.instant('JURISDICTION_APPLY_REGIME_RESTORED', { regime })
            : this._translate.instant('JURISDICTION_DIFF_REGIME_UNAVAILABLE', { regime, country });
        return `«${variant_name}»: ${phrase}`;
      })
      .join('; ');
    return { text, type: changes.some(({ kind }) => kind !== 'restore') ? 'error' : 'success' };
  }

  // NOTE: уведомление после apply (основные данные, плашка F8, "Вернуть по юрисдикции"): по режиму или обычное
  notifyApplied(result: IJurisdictionApplyResult, diff?: IJurisdictionDiff | null) {
    const notice = this.getRegimeChangesNotice(result, diff);
    if (notice) {
      this._snackbarService.openSnackBar(notice.text, notice.type);
      return;
    }
    this._snackbarService.openSnackBar(this._translate.instant('JURISDICTION_DIFF_APPLIED'), 'success');
  }

  // NOTE: F4/F6 - ставки НДС в формах объектов и лизинга блокируются, если первый налоговый вариант проекта
  // на минимальном режиме (tax_simulation = 0) и НДС на нём не считается. Признак "плательщик НДС" отдаёт
  // tax_options.vat_enabled; режим первого варианта нужен, только когда НДС выключен (на спецрежимах не блокируем)
  isVatDisabledByMinTax(
    projectId: number,
    taxOptions$: Observable<ITaxOptions> = this.getTaxOptions(projectId),
  ): Observable<boolean> {
    return taxOptions$.pipe(
      switchMap(({ vat_enabled }) => {
        if (vat_enabled !== false) return of(false);
        return this._http
          .get<{ results: { tax_simulation: number }[] }>(`${this._baseUrl}/tax_prms/`, {
            params: { project: projectId },
          })
          .pipe(map(({ results }) => results?.[0]?.tax_simulation === 0));
      }),
    );
  }

  // NOTE: страна проекта нужна, чтобы отличить "страна не выбрана" от страны без своей юрисдикции
  getProjectCountry(projectId: number): Observable<number | null> {
    return this._http
      .get<{ country: number | null }>(`${this._projectsUrl}/${projectId}/`)
      .pipe(map(({ country }) => country ?? null));
  }
}
